import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Autocomplete from 'react-google-autocomplete';
import { StanInput, StanModal } from '@/components';
import { parseGooglePlaceAddress } from '@/features/email-flows/logic/emailFlows.logic';
import { type AddressFormData, addressSchema } from '@/features/email-flows/validations/address';
import { configSettings } from '@/lib/config';
import styles from '@/components/core-components/StanInput/StanInput.module.css';
import { cn } from '@/lib/utils/cn';
import type { TaxAddress } from '@/lib/api/settings';

interface TaxAddressModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (address: TaxAddress) => void;
    initialAddress: TaxAddress;
    loading?: boolean;
}

interface PlaceResult {
    address_components?: Array<{
        long_name: string;
        short_name: string;
        types: string[];
    }>;
}

export const TaxAddressModal: React.FC<TaxAddressModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    initialAddress,
    loading = false,
}) => {
    const [isFocused, setIsFocused] = useState(false);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        watch,
    } = useForm<AddressFormData>({
        resolver: zodResolver(addressSchema),
        defaultValues: initialAddress,
    });

    const watchedValues = watch();

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            reset(initialAddress);
        }
    }, [isOpen, initialAddress, reset]);

    const handlePlaceSelected = useCallback(
        (place: PlaceResult) => {
            if (!place.address_components) {
                return;
            }

            const parsedAddress = parseGooglePlaceAddress(place.address_components);

            setValue('street', parsedAddress.street, { shouldValidate: true });
            setValue('city', parsedAddress.city, { shouldValidate: true });
            setValue('state', parsedAddress.state, { shouldValidate: true });
            setValue('postal_code', parsedAddress.postal_code, { shouldValidate: true });
            setValue('country', parsedAddress.country, { shouldValidate: true });
        },
        [setValue]
    );

    const onFormSubmit = useCallback(
        (data: AddressFormData) => {
            onSubmit(data);
        },
        [onSubmit]
    );

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    return (
        <StanModal
            id='tax-address-modal'
            isOpen={isOpen}
            onClose={handleClose}
            title='Tax Address'
            size='sm'
            primaryActionLabel='Update'
            primaryActionLoading={loading}
            onPrimaryAction={handleSubmit(onFormSubmit)}
        >
            <div className='space-y-4'>
                <p className='text-body-sm text-gray text-center'>
                    The place where your business is located, or the origin address, may determine where you are
                    required to collect sales tax, VAT, or GST.
                </p>

                <form className='space-y-4'>
                    {/* Street Address with Google Autocomplete */}
                    <Controller
                        name='street'
                        control={control}
                        render={({ field }) => (
                            <div
                                className={cn(
                                    'stan-input',
                                    errors.street && styles.negative,
                                    loading && styles.disabled
                                )}
                            >
                                <label className={styles.label} style={{ marginBottom: '0.5rem' }}>
                                    Street Address
                                </label>
                                <div
                                    className={cn('p-0!', styles.wrapper, isFocused && styles.active)}
                                    onMouseEnter={e => {
                                        if (!loading) {
                                            e.currentTarget.style.borderColor = 'var(--bg-color-primary-brand-main)';
                                        }
                                    }}
                                    onMouseLeave={e => {
                                        if (!isFocused && !errors.street) {
                                            e.currentTarget.style.borderColor = 'var(--border-color-disabled)';
                                        }
                                    }}
                                >
                                    <Autocomplete
                                        apiKey={configSettings.googleMapsApiKey}
                                        onPlaceSelected={handlePlaceSelected}
                                        options={{
                                            types: ['address'],
                                        }}
                                        className={cn('h-full px-4 py-3', styles.field)}
                                        placeholder='Start typing your address...'
                                        value={field.value}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            field.onChange(e.target.value)
                                        }
                                        onFocus={() => setIsFocused(true)}
                                        onBlur={() => setIsFocused(false)}
                                        disabled={loading}
                                        autoComplete='off'
                                    />
                                </div>
                                {errors.street && <span className={styles.hint}>{errors.street.message}</span>}
                            </div>
                        )}
                    />

                    {/* City */}
                    <Controller
                        name='city'
                        control={control}
                        render={({ field }) => (
                            <StanInput
                                {...field}
                                name='tax-address-city'
                                topLabel='City'
                                error={!!errors.city}
                                hintText={errors.city?.message}
                                disabled={loading}
                            />
                        )}
                    />

                    {/* State and Postal Code */}
                    <div className='grid grid-cols-2 gap-4'>
                        <Controller
                            name='state'
                            control={control}
                            render={({ field }) => (
                                <StanInput
                                    {...field}
                                    name='tax-address-state'
                                    topLabel='State/Province'
                                    error={!!errors.state}
                                    hintText={errors.state?.message}
                                    disabled={loading}
                                />
                            )}
                        />
                        <StanInput
                            {...register('postal_code')}
                            name='tax-address-postal'
                            value={watchedValues.postal_code || ''}
                            topLabel='Postal Code'
                            error={!!errors.postal_code}
                            hintText={errors.postal_code?.message}
                            disabled={loading}
                        />
                    </div>

                    {/* Country */}
                    <StanInput
                        {...register('country')}
                        name='tax-address-country'
                        value={watchedValues.country || ''}
                        topLabel='Country'
                        error={!!errors.country}
                        hintText={errors.country?.message}
                        disabled={loading}
                    />
                </form>
            </div>
        </StanModal>
    );
};
